import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatDate, roleLabel } from "@/lib/statusUtils";
import { toast } from "sonner";
import { UserCheck, UserPlus, UserMinus, History as HistoryIcon, AlertTriangle, RefreshCw, Search, Users } from "lucide-react";

const ASSIGNMENT_META = {
  assigned: { bg: "#34735520", color: "#347355", label: "Assigned" },
  transferred: { bg: "#C06C4720", color: "#C06C47", label: "Transferred" },
  unassigned: { bg: "#EAB30820", color: "#B08B00", label: "Unassigned" },
  inactive: { bg: "#5C665320", color: "#5C6653", label: "Inactive" },
};

export default function AdminAssignments() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [adminFilter, setAdminFilter] = useState("all");
  const [assignFilter, setAssignFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialogs
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm, setAssignForm] = useState({ admin_id: "", notes: "" });
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignForm, setReassignForm] = useState({ new_admin_id: "", reason: "", notes: "" });
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassignReason, setUnassignReason] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: { include_stats: true } });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const admins = useMemo(() => users.filter((u) => u.role === "admin"), [users]);
  const cps = useMemo(() => users.filter((u) => u.role === "agent"), [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cps.filter((c) => {
      if (statusFilter !== "all" && (c.status !== statusFilter && !(statusFilter === "active" && (c.status === "approved" || c.status === "active")))) return false;
      if (adminFilter === "__unassigned") { if (c.assigned_admin_id) return false; }
      else if (adminFilter !== "all" && c.assigned_admin_id !== adminFilter) return false;
      if (assignFilter !== "all") {
        const eff = c.assigned_admin_id ? (c.assignment_status || "assigned") : "unassigned";
        if (eff !== assignFilter) return false;
      }
      if (!q) return true;
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.mobile || "").toLowerCase().includes(q)
      );
    });
  }, [cps, search, adminFilter, assignFilter, statusFilter]);

  const stats = useMemo(() => ({
    total_cps: cps.length,
    assigned: cps.filter((c) => c.assigned_admin_id).length,
    unassigned: cps.filter((c) => !c.assigned_admin_id).length,
    admins: admins.length,
  }), [cps, admins]);

  // --- Actions ---
  const openAssign = (cp) => { setAssignTarget(cp); setAssignForm({ admin_id: "", notes: "" }); };
  const confirmAssign = async () => {
    if (!assignForm.admin_id) { toast.error("Select an admin"); return; }
    try {
      await api.post(`/users/${assignTarget.id}/assign-admin`, assignForm);
      toast.success("Channel Partner assigned");
      setAssignTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReassign = (cp) => { setReassignTarget(cp); setReassignForm({ new_admin_id: "", reason: "", notes: "" }); };
  const confirmReassign = async () => {
    if (!reassignForm.new_admin_id) { toast.error("Select a new admin"); return; }
    if (!reassignForm.reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/users/${reassignTarget.id}/reassign-admin`, reassignForm);
      toast.success("Channel Partner reassigned");
      setReassignTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openUnassign = (cp) => { setUnassignTarget(cp); setUnassignReason(""); };
  const confirmUnassign = async () => {
    try {
      await api.post(`/users/${unassignTarget.id}/unassign-admin`, { reason: unassignReason });
      toast.success("Assignment removed");
      setUnassignTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async (cp) => {
    setHistoryTarget(cp); setHistoryEntries([]);
    try {
      const { data } = await api.get(`/users/${cp.id}/assignment-history`);
      setHistoryEntries(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Super Admin</div>
            <h1 className="font-heading text-2xl md:text-4xl">Admin & Channel Partner Assignments</h1>
            <p className="text-sm text-[#5C6653] mt-2">Assign each Channel Partner to exactly one Admin. Admins can only see and manage the CPs assigned to them.</p>
          </div>
          <Button onClick={load} variant="outline" className="rounded-sm w-fit" data-testid="refresh-assignments-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="assignment-kpis">
          <StatCard label="Total Channel Partners" value={stats.total_cps} icon={Users} />
          <StatCard label="Assigned" value={stats.assigned} accent="#347355" icon={UserCheck} />
          <StatCard label="Unassigned" value={stats.unassigned} accent="#B08B00" icon={UserPlus} />
          <StatCard label="Admins" value={stats.admins} icon={Users} />
        </div>

        {/* Filters */}
        <div className="border border-[#E5E2DC] bg-white p-4 flex flex-wrap gap-3 mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6653]" />
            <Input placeholder="Search name / email / mobile" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="assignments-search" className="pl-9 w-full sm:w-64 rounded-sm" />
          </div>
          <Select value={adminFilter} onValueChange={setAdminFilter}>
            <SelectTrigger className="w-full sm:w-56 rounded-sm" data-testid="assignments-admin-filter"><SelectValue placeholder="Assigned admin" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All admins</SelectItem>
              <SelectItem value="__unassigned">Unassigned only</SelectItem>
              {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={assignFilter} onValueChange={setAssignFilter}>
            <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="assignments-status-filter"><SelectValue placeholder="Assignment status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignments</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="transferred">Transferred</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="assignments-cp-status-filter"><SelectValue placeholder="CP status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All CP statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="pending_approval">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-[#5C6653] self-center whitespace-nowrap">
            <span className="font-mono-anu text-[#1A1F16]">{filtered.length}</span> channel partners
          </div>
        </div>

        <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]" data-testid="assignments-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Channel Partner</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Assigned Admin</Th>
                <Th>Assignment</Th>
                <Th>Bookings (P·A·T)</Th>
                <Th>Last Activity</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#5C6653]">No Channel Partners match your filters.</td></tr>
              ) : (
                filtered.map((cp) => {
                  const bs = cp.booking_stats || { total: 0, pending: 0, approved: 0 };
                  const isAssigned = !!cp.assigned_admin_id;
                  const asMeta = isAssigned
                    ? ASSIGNMENT_META[cp.assignment_status || "assigned"]
                    : ASSIGNMENT_META.unassigned;
                  return (
                    <tr key={cp.id} className="border-b border-[#E5E2DC]" data-testid={`cp-row-${cp.email}`}>
                      <Td>
                        <div className="font-medium">{cp.name}</div>
                        {cp.company_name && <div className="text-xs text-[#5C6653]">{cp.company_name}</div>}
                      </Td>
                      <Td>
                        <div className="text-xs">{cp.email}</div>
                        <div className="text-xs text-[#5C6653]">{cp.mobile}</div>
                      </Td>
                      <Td>
                        <span className="text-[10px] uppercase tracking-wider text-[#5C6653]">{normalize(cp.status)}</span>
                      </Td>
                      <Td>
                        {isAssigned ? (
                          <>
                            <div className="text-sm">{cp.assigned_admin_name}</div>
                            <div className="text-xs text-[#5C6653]">{cp.assigned_admin_email}</div>
                          </>
                        ) : <span className="text-xs text-[#5C6653]">—</span>}
                      </Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: asMeta.bg, borderColor: asMeta.color, color: asMeta.color }}>{asMeta.label}</span>
                        {cp.assigned_at && <div className="text-[10px] text-[#5C6653] mt-1">{formatDate(cp.assigned_at)}</div>}
                      </Td>
                      <Td>
                        <div className="text-xs">
                          <span className="text-[#B08B00]">{bs.pending || 0}</span>
                          {" · "}<span className="text-[#347355]">{bs.approved || 0}</span>
                          {" · "}<span className="font-medium">{bs.total || 0}</span>
                        </div>
                      </Td>
                      <Td><span className="text-xs text-[#5C6653]">{cp.last_activity ? formatDate(cp.last_activity) : "—"}</span></Td>
                      <Td>
                        <div className="flex gap-1.5 flex-wrap">
                          {!isAssigned && (
                            <Button size="sm" onClick={() => openAssign(cp)} data-testid={`assign-${cp.email}`} className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-8">
                              <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
                            </Button>
                          )}
                          {isAssigned && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openReassign(cp)} data-testid={`reassign-${cp.email}`} className="rounded-sm h-8">
                                <UserCheck className="h-3.5 w-3.5 mr-1" /> Reassign
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openUnassign(cp)} data-testid={`unassign-${cp.email}`} className="rounded-sm h-8 border-red-300 text-red-700 hover:bg-red-50">
                                <UserMinus className="h-3.5 w-3.5 mr-1" /> Remove
                              </Button>
                            </>
                          )}
                          <button onClick={() => openHistory(cp)} data-testid={`assign-history-${cp.email}`} title="History"
                            className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm">
                            <HistoryIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="assign-dialog">
          {assignTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#234B36]" /> Assign Channel Partner</DialogTitle>
                <DialogDescription>Assign <span className="font-medium text-[#1A1F16]">{assignTarget.name}</span> ({assignTarget.email}) to an Admin.</DialogDescription>
              </DialogHeader>
              <FormRow label="Admin" required>
                <Select value={assignForm.admin_id} onValueChange={(v) => setAssignForm({ ...assignForm, admin_id: v })}>
                  <SelectTrigger data-testid="assign-admin-select" className="rounded-sm"><SelectValue placeholder="Select admin" /></SelectTrigger>
                  <SelectContent>
                    {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Notes (optional)">
                <Textarea rows={3} value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} data-testid="assign-notes" className="rounded-sm" />
              </FormRow>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAssignTarget(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={confirmAssign} data-testid="assign-confirm" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">Confirm</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign */}
      <Dialog open={!!reassignTarget} onOpenChange={(o) => !o && setReassignTarget(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="reassign-dialog">
          {reassignTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2"><UserCheck className="h-5 w-5 text-[#C06C47]" /> Reassign Channel Partner</DialogTitle>
                <DialogDescription>Move <span className="font-medium text-[#1A1F16]">{reassignTarget.name}</span> from <span className="font-medium">{reassignTarget.assigned_admin_name}</span> to another Admin.</DialogDescription>
              </DialogHeader>
              <div className="flex items-start gap-2 text-sm text-orange-800 bg-orange-50 border border-orange-200 px-3 py-2 rounded-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>All active bookings and buyers of this Channel Partner will become visible to the new Admin. Past records remain preserved.</div>
              </div>
              <FormRow label="New Admin" required>
                <Select value={reassignForm.new_admin_id} onValueChange={(v) => setReassignForm({ ...reassignForm, new_admin_id: v })}>
                  <SelectTrigger data-testid="reassign-admin-select" className="rounded-sm"><SelectValue placeholder="Select new admin" /></SelectTrigger>
                  <SelectContent>
                    {admins.filter((a) => a.id !== reassignTarget.assigned_admin_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Reason" required>
                <Input value={reassignForm.reason} onChange={(e) => setReassignForm({ ...reassignForm, reason: e.target.value })} data-testid="reassign-reason" className="rounded-sm" />
              </FormRow>
              <FormRow label="Notes (optional)">
                <Textarea rows={2} value={reassignForm.notes} onChange={(e) => setReassignForm({ ...reassignForm, notes: e.target.value })} data-testid="reassign-notes" className="rounded-sm" />
              </FormRow>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReassignTarget(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={confirmReassign} data-testid="reassign-confirm" className="rounded-sm bg-[#C06C47] hover:bg-[#A85936] text-white">Reassign</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Unassign */}
      <Dialog open={!!unassignTarget} onOpenChange={(o) => !o && setUnassignTarget(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="unassign-dialog">
          {unassignTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2"><UserMinus className="h-5 w-5 text-red-700" /> Remove assignment</DialogTitle>
                <DialogDescription>
                  Unassign <span className="font-medium text-[#1A1F16]">{unassignTarget.name}</span> from <span className="font-medium">{unassignTarget.assigned_admin_name}</span>. Bookings & buyer history stay intact.
                </DialogDescription>
              </DialogHeader>
              <FormRow label="Reason (optional)">
                <Textarea rows={3} value={unassignReason} onChange={(e) => setUnassignReason(e.target.value)} data-testid="unassign-reason" className="rounded-sm" />
              </FormRow>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUnassignTarget(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={confirmUnassign} data-testid="unassign-confirm" className="rounded-sm bg-red-700 hover:bg-red-800 text-white">Remove</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="assign-history-dialog">
          {historyTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2"><HistoryIcon className="h-5 w-5" /> Assignment history · {historyTarget.email}</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto border border-[#E5E2DC] rounded-sm">
                {historyEntries.length === 0 ? (
                  <div className="text-center py-8 text-sm text-[#5C6653]">No assignment history yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F0EFEA] sticky top-0"><tr><Th>When</Th><Th>Action</Th><Th>By</Th><Th>Details</Th></tr></thead>
                    <tbody>
                      {historyEntries.map((h) => (
                        <tr key={h.id} className="border-t border-[#E5E2DC]">
                          <Td><span className="text-xs">{formatDate(h.timestamp)}</span></Td>
                          <Td><span className="text-xs font-medium">{h.action}</span></Td>
                          <Td>
                            <div className="text-xs">{h.actor_name || h.actor_email || "—"}</div>
                            {h.actor_role && <div className="text-[10px] uppercase text-[#5C6653]">{roleLabel(h.actor_role)}</div>}
                          </Td>
                          <Td><pre className="text-[11px] whitespace-pre-wrap font-mono-anu text-[#3B4436]">{h.details ? JSON.stringify(h.details, null, 2) : "—"}</pre></Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

const normalize = (s) => (s === "approved" ? "active" : s === "pending" ? "pending_approval" : s || "active");

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function FormRow({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-medium mb-1 block">{label}{required && <span className="text-[#C06C47]"> *</span>}</Label>
      {children}
    </div>
  );
}
function StatCard({ label, value, sub, accent, icon: Icon }) {
  return (
    <div className="border border-[#E5E2DC] bg-white p-5 flex flex-col justify-between min-h-[100px]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold">{label}</div>
        {Icon && <Icon className="h-4 w-4" style={{ color: accent || "#234B36" }} />}
      </div>
      <div className="font-heading text-3xl mt-2" style={{ color: accent || "#1A1F16" }}>{value}</div>
      {sub && <div className="text-xs text-[#5C6653] mt-1">{sub}</div>}
    </div>
  );
}
